import React, { useMemo } from 'react';
import { useTable, useSortBy } from 'react-table';
import './index.scss';
import { Col, Row } from 'react-bootstrap';
import {
  CustomPagination,
  CustomPaginationItemsDisplay,
} from '../../common/CustomPagination';
import moment from 'moment';
import GenericButton from '../../common/button';
import { AuditDownloadIcon } from 'src/Admin/img/permissionsAuditLog';
import { Tooltip } from '@material-ui/core';
import './EnumerationAuditLogList.scss';
import Skeleton from '../../common/skeleton';

const renderCell = ({ value }) => (
  <div className="ellipsis-cell text-capitalize" title={value}>
    {!value || ['NA', 'Na'].includes(value) ? 'NA' : value}
  </div>
);

const EnumerationAuditLogList = ({
  data,
  downloadAuditListData,
  getAuditListData,
  auditDataFetching,
}) => {
  const columns = useMemo(
    () => [
      {
        Header: 'Date and Time',
        accessor: 'createdTs',
        width: '14rem',
        Cell: ({ row, value }) => {
          return (
            <span>
              {value ? moment(value).format('MM/DD/YYYY hh:mm:ss A') : 'NA'}
            </span>
          );
        },
      },
      {
        Header: 'User ID',
        accessor: 'name',
        width: '12rem',
        Cell: ({ row, value }) => (
          <Tooltip
            placement="top"
            title={
              <div className="my-resources-container">
                <div>{row?.original?.email}</div>
              </div>
            }
            arrow
          >
            {renderCell({ value })}
          </Tooltip>
        ),
      },
      {
        Header: 'User Type',
        accessor: 'usertype',
        width: '8rem',
        Cell: renderCell,
      },
      {
        Header: 'Action Type',
        accessor: 'action',
        width: '16rem',
        Cell: renderCell,
      },
      {
        Header: 'Verification Method',
        width: '14rem',
        accessor: 'verificationMethod',
        Cell: renderCell,
      },
      {
        Header: 'OTP',
        accessor: 'otpUsed',
        width: '6rem',
        Cell: renderCell,
      },
      {
        Header: 'Failed Attempt Count',
        accessor: 'failedAttemptCount',
        width: '14rem',
        Cell: renderCell,
      },
      {
        Header: 'Cool‑Off Period',
        accessor: 'coolOffPeriod',
        width: '11rem',
        Cell: renderCell,
      },
      {
        Header: 'IP Address',
        accessor: 'ipAddress',
        width: '10rem',
        Cell: renderCell,
      },
      {
        Header: 'Browser',
        accessor: 'browser',
        width: '8rem',
        Cell: renderCell,
      },
      {
        Header: 'Session ID',
        accessor: 'sessionId',
        width: '20rem',
        Cell: renderCell,
      },
    ],
    [],
  );

  const tableInstance = useTable(
    {
      columns,
      data: data?.Response || [],
    },
    useSortBy,
  );

  const showingRecordsMessage = useMemo(() => {
    if (!data) return '';
    const startRecord = (data.Page - 1) * data.Size + 1;
    const endRecord = Math.min(startRecord + data.Size - 1, data.TotalElements);

    return `Showing ${startRecord} - ${endRecord} of ${data.TotalElements} results`;
  }, [data]);

  const renderAuditData = (cell) => {
    if (cell.column.Cell) {
      return cell.render('Cell');
    }

    return cell.value;
  };

  const getPaginationComponent = useMemo(() => {
    return (
      <Row className="mt-4 mb-4 align-items-center">
        {data?.TotalElements > 10 && (
          <>
            <Col sm={6}>
              <CustomPaginationItemsDisplay
                pageSize={data?.Size || 10}
                setPageSize={(e) =>
                  getAuditListData({
                    pagenumber: data?.Page || 1,
                    pagesize: Number(e?.value),
                  })
                }
                prefixMessage="Records per page"
                suffixMessage={showingRecordsMessage}
              />
            </Col>
            <Col sm={6} className="text-right mt-4 mt-md-0">
              <div className="reports-table-pagination">
                {data.TotalElements > 10 ? (
                  <CustomPagination
                    pageSize={data?.Size || 10}
                    currentPage={data?.Page || 1}
                    totalCount={data.TotalElements}
                    onPageChange={(page) =>
                      getAuditListData({
                        pagenumber: page,
                        pagesize: data?.Size || 10,
                      })
                    }
                  />
                ) : null}
              </div>
            </Col>
          </>
        )}
      </Row>
    );
  }, [data, showingRecordsMessage]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    tableInstance;

  return (
    <div className="enum-audit-history-table">
      <div>
        {getPaginationComponent}
        <div className="text-right audit-history-download">
          <GenericButton
            variant="secondary"
            className="mb-4"
            onClick={() => downloadAuditListData()}
          >
            <AuditDownloadIcon className="download-icon" />
            Download
          </GenericButton>
        </div>
        <div className="audit-table-scroll">
          <table {...getTableProps()} className="table padding-mobile-0">
            <thead>
              {headerGroups.map((headerGroup, headerIndex) => (
                <tr
                  key={headerIndex}
                  {...headerGroup.getHeaderGroupProps()}
                  role="row"
                >
                  {headerGroup.headers.map((column, columnIndex) => (
                    <th
                      key={columnIndex}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      scope="col"
                      role="columnheader"
                      style={{
                        width: column.width,
                        paddingLeft: column.paddingLeft,
                        paddingRight: column.paddingRight,
                      }}
                      className={
                        column.isSorted ? 'coloumnIsSorted' : 'coloumnNotSorted'
                      }
                      onKeyPress={(event) => {
                        if (
                          column.sortable !== false &&
                          event.key === 'Enter'
                        ) {
                          column.toggleSortBy();
                        }
                      }}
                    >
                      <div className="th-content">
                        <span>{column.render('Header')}</span>
                        {column?.sortable !== false && (
                          <span
                            className={
                              column.isSorted
                                ? column.isSortedDesc
                                  ? 'arrowDown'
                                  : 'arrowUp'
                                : 'arrowNormal'
                            }
                            tabIndex={0}
                            aria-label={
                              column.isSorted
                                ? column.isSortedDesc
                                  ? ` ${column.Header} sorted descending`
                                  : ` ${column.Header} sorted ascending`
                                : ` ${column.Header} sorted none`
                            }
                            aria-live="polite"
                            aria-sort={
                              column.isSorted
                                ? column.isSortedDesc
                                  ? 'descending'
                                  : 'ascending'
                                : 'none'
                            }
                          ></span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()} role="rowgroup">
              {auditDataFetching
                ? Array.from({ length: data?.Size || 10 }, (_, i) => (
                    <tr key={`skleton-${i}`}>
                      {columns?.map((col) => (
                        <td key={`skeleton-${col.accessor}`}>
                          <Skeleton width={'100%'} height="2rem" />
                        </td>
                      ))}
                    </tr>
                  ))
                : rows.map((row, rowIndex) => {
                    prepareRow(row);

                    return (
                      <tr key={rowIndex} {...row.getRowProps()}>
                        {row.cells.map((cell, index) => {
                          return (
                            <td
                              key={index}
                              {...cell.getCellProps()}
                              role="cell"
                              style={{
                                maxWidth: cell.column.width,
                                paddingLeft: cell.column.paddingLeft,
                              }}
                            >
                              <p className="reports-table-cell-data-admin">
                                {renderAuditData(cell)}
                              </p>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
        {getPaginationComponent}
      </div>
    </div>
  );
};

export default EnumerationAuditLogList;
